/**
 * A kind of event's starter room, small.
 *
 * Drawn from `STARTER_ROOMS` through the same geometry the canvas and the PDF
 * use, so the picture in the chooser is the room the plan really opens on.
 * Colours are the canvas's CSS variables, so it follows light and dark.
 */

import { memo } from 'react'

import { screenSwatchFor } from '../lib/colors'
import {
  SEAT_RADIUS,
  computeTableGeometry,
  seatWorldPositions,
  tableBounds,
  unionBounds,
} from '../lib/geometry'
import { STARTER_ROOMS } from '../lib/templates'
import type { EventKind } from '../lib/types'

const PAD = 36

export const RoomThumb = memo(function RoomThumb({ kind }: { kind: EventKind }) {
  const room = STARTER_ROOMS[kind]
  const box = unionBounds(
    room.map((table) => tableBounds(table.shape, table.seats, table.x, table.y, table.rotation)),
  )
  if (!box) return null
  const viewBox = [
    box.minX - PAD,
    box.minY - PAD,
    box.maxX - box.minX + PAD * 2,
    box.maxY - box.minY + PAD * 2,
  ].join(' ')

  return (
    <svg className="room-thumb" viewBox={viewBox} aria-hidden="true" focusable="false">
      {room.map((table, index) => {
        const swatch = screenSwatchFor(table.color)
        const geometry = computeTableGeometry(table.shape, table.seats)
        const top = { fill: swatch.surface, stroke: swatch.edge }
        return (
          <g key={index}>
            {seatWorldPositions(table.shape, table.seats, table.x, table.y, table.rotation).map(
              (seat) => (
                <circle
                  key={seat.index}
                  cx={seat.x}
                  cy={seat.y}
                  r={SEAT_RADIUS}
                  style={{ fill: swatch.seat, stroke: swatch.seatEdge }}
                  strokeWidth={4}
                />
              ),
            )}
            {table.shape === 'round' ? (
              <circle cx={table.x} cy={table.y} r={geometry.radius} style={top} strokeWidth={5} />
            ) : (
              <rect
                x={table.x - geometry.width / 2}
                y={table.y - geometry.height / 2}
                width={geometry.width}
                height={geometry.height}
                rx={table.shape === 'square' ? 14 : 18}
                transform={`rotate(${table.rotation} ${table.x} ${table.y})`}
                style={top}
                strokeWidth={5}
              />
            )}
          </g>
        )
      })}
    </svg>
  )
})
